import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  Divider,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Stack,
  Checkbox,
  ListItemText,
  InputAdornment,
  Autocomplete,
  Switch,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Eye, EyeOff, Search, UploadIcon, UserPlus, X } from "lucide-react";
import AddIcon from "@mui/icons-material/Add";
import { axiosCall } from "../utils/axiosCall";
import constants from "../config/constants";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "../redux/features/userSlice";
import { useNavigate } from "react-router-dom";
import RolePermissions from "../comps/RolePermissions";

const Settings = () => {
  const [showPassword, setShowPassword] = useState(false);
  const user = useSelector((state) => state.user);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [staff, setStaff] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNo: "",
    password: "",
    confirmPassword: "",
  });

  const handleInputChange = (e) => {
    setStaff({
      ...staff,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    getRoles();
    getStaff();
  }, []);

  const getRoles = async () => {
    try {
      const roles = await axiosCall(
        constants.GET_METHOD,
        constants.GET_ROLES,
        null,
        null,
        user.token
      );

      if (roles.isSuccess) {
        let filteredRoles = roles.data.data;
        
        // Filter roles based on user's access level
        if (user.role === 'Super Admin') {
          // Super Admin can see all roles
          filteredRoles = roles.data.data;
        } else if (user.role === 'Admin') {
          // Admin can see all roles except Super Admin
          filteredRoles = roles.data.data.filter(role => role.role !== 'Super Admin');
        } else {
          // Other roles shouldn't manage roles, but if they access, show minimal
          filteredRoles = [];
        }
        
        setRoles(filteredRoles);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getStaff = async () => {
    try {
      const staff = await axiosCall(
        constants.GET_METHOD,
        constants.GET_USER + `/${user.id}`,
        null,
        null,
        user.token
      );

      if (staff.isSuccess) {
        setStaff(staff.data.data);

        setLoading(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const permissionOptions = [
    { permission: "Appointments" },
    { permission: "Consultation" },
    { permission: "Patients" },
    { permission: "Reports" },
  ];

  // const roles = ['Nurse', 'HR', 'Doctors', 'HOD'];

  const fileInputRef = useRef(null);
  const fileInputEditRef = useRef(null);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUploadEditClick = () => {
    if (fileInputEditRef.current) {
      fileInputEditRef.current.click();
    }
  };

  const formData = new FormData();
  const handleUpload = async (e) => {
    formData.append("type", "staff");
    formData.append("image", e.target.files[0]);

    const uploadPic = await axiosCall(
      constants.POST_METHOD,
      constants.UPLOAD_IMAGE,
      formData,
      null,
      user.token
    );

    if (uploadPic.isSuccess) {
      setStaff({
        ...staff,
        profilePicture: uploadPic.data?.data?.Location,
      });
    } else {
      alert(uploadPic.error?.message);
    }
  };

  const handleUserUpdate = async () => {
    if (staff.password !== staff.confirmPassword) {
      alert("Password and confirm password do not match");
      return;
    }
    setLoading(true);
    const update = await axiosCall(
      constants.PUT_METHOD,
      constants.UPDATE_USER + `/${user.id}`,
      {
        ...staff,
        loginPassword: staff.password,
      },
      null,
      user.token
    );

    if (update.isSuccess) {
      if (staff.password) {
        alert(
          "User updated and password updated successfully. Please login with new password."
        );
        dispatch(logOut());
        navigate("/");
      } else {
        alert("User updated successfully");
        setLoading(false);
      }
    } else {
      alert(update.error.message);
    }
  };

  const handlePermissionsChange = (role, newPermissions) => {
    const updatedPermissionsState = roles.map((r) =>
      r.role === role ? { ...r, permissions: newPermissions } : r
    );
    setRoles(updatedPermissionsState);
  };

  const handleRoleUpdate = async () => {
    setLoading(true);
    roles.forEach(async (role) => {
      const update = await axiosCall(
        constants.PUT_METHOD,
        constants.UPDATE_ROLE + `/${role._id}`,
        { permissions: role.permissions },
        null,
        user.token
      );

      if (update.isSuccess) {
      } else {
        alert(update.error.message);
      }
    });
    alert("Role permissions updated successfully");
    setLoading(false);
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        {loading ? (
          <h2>Loading...</h2>
        ) : (
          <Box sx={{ width: "100%" }}>
            {/* Form */}

            <Box
              sx={{
                padding: 2,
                borderRadius: 2,
                marginTop: 2,
                marginRight: "90px",
              }}
            >
              {/* Heading */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  my: '20px',
                  color: '#5D6A82',
                }}
              >
                <Typography sx={{ 
                  color: "#5D6A82", 
                  fontFamily: "Open Sans",
                  fontWeight: 600,
                  fontSize: "22px",
                  lineHeight: "29px",
                  letterSpacing: "0px"
                }}>User Account Settings</Typography>
              </Box>

              {/* User Information */}
              <Box
                sx={{
                  backgroundColor: "#f5faff",
                  padding: 2,
                  borderRadius: 2,
                  marginBottom: 2,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: "25rem",
                    display: "flex",
                    flexDirection: "column",
                    align: "center",
                    // justifyContent: "space-evenly",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "15rem",
                      border: "1px solid black",
                      borderRadius: 2,
                      marginRight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {staff.profilePicture && (
                      <>
                        <img
                          src={staff.profilePicture}
                          alt="User Profile"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <Button
                          variant=""
                          sx={{
                            color: "#5d6a82",
                            background: "#dfe8ef",
                            position: "absolute",
                            bottom: 8,
                            right: 8,
                          }}
                          onClick={handleUploadEditClick}
                        >
                          Edit
                        </Button>
                        <input
                          type="file"
                          ref={fileInputEditRef}
                          style={{ display: "none" }}
                          onChange={(e) => handleUpload(e)}
                          inputProps={{ accept: "image/*" }}
                        />
                      </>
                    )}
                  </Box>
                  {!staff.profilePicture && (
                    <>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={(e) => handleUpload(e)}
                        inputProps={{ accept: "image/*" }}
                      />
                      <Button
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          width: "100%",
                          height: "3rem",
                          color: "#0179B0",
                          textTransform: "none",
                        }}
                        onClick={handleUploadClick}
                      >
                        Upload
                      </Button>
                    </>
                  )}
                </Box>

                <Box
                  sx={{
                    // padding: 2,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ display: "flex", gap: 1, flex: "1 1 48%" }}>
                      <FormControl sx={{ minWidth: 80 }}>
                        <InputLabel
                          sx={{ backgroundColor: "#f5faff", padding: "0px 4px" }}
                        >
                          Title
                        </InputLabel>
                        <Select
                          value={staff.title || ""}
                          onChange={(e) => handleInputChange(e)}
                          name="title"
                          size="medium"
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="Mr.">Mr.</MenuItem>
                          <MenuItem value="Mrs.">Mrs.</MenuItem>
                          <MenuItem value="Dr.">Dr.</MenuItem>
                          <MenuItem value="Ms.">Ms.</MenuItem>
                          <MenuItem value="Prof.">Prof.</MenuItem>
                          <MenuItem value="Er.">Er.</MenuItem>
                          <MenuItem value="Sister">Sister</MenuItem>
                          <MenuItem value="Rev.">Rev.</MenuItem>
                          <MenuItem value="Capt.">Capt.</MenuItem>
                          <MenuItem value="Col.">Col.</MenuItem>
                          <MenuItem value="Maj.">Maj.</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="First Name"
                        sx={{ flex: 1 }}
                        value={staff.firstName}
                        onChange={(e) => handleInputChange(e)}
                        name="firstName"
                      />
                    </Box>
                    <TextField
                      label="Last Name"
                      sx={{ flex: "1 1 48%" }}
                      value={staff.lastName}
                      onChange={(e) => handleInputChange(e)}
                      name="lastName"
                    />
                    <TextField
                      label="Email"
                      sx={{ flex: "1 1 100%" }}
                      value={staff.email}
                      onChange={(e) => handleInputChange(e)}
                      name="email"
                    />
                    <TextField
                      label="Contact Number"
                      sx={{ flex: "1 1 100%" }}
                      value={staff.contactNo}
                      onChange={(e) => handleInputChange(e)}
                      name="contactNo"
                    />

                    <TextField
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      sx={{ flex: "1 1 Calc(50% - 8px)" }}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <EyeOff /> : <Eye />}
                          </IconButton>
                        ),
                      }}
                      onChange={(e) => handleInputChange(e)}
                      name="password"
                      value={staff.password}
                    />

                    <TextField
                      label="Confirm Password"
                      type={showPassword ? "text" : "password"}
                      sx={{ flex: "1 1 Calc(50% - 8px)" }}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <EyeOff /> : <Eye />}
                          </IconButton>
                        ),
                      }}
                      onChange={(e) => handleInputChange(e)}
                      name="confirmPassword"
                      value={staff.confirmPassword}
                    />

                    {/* <FormControlLabel
                    control={
                      <Switch
                        focusVisibleClassName=".Mui-focusVisible"
                        disableRipple
                      />
                    }
                    label="Two-Factor Authentication"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        color: "#92A6CC",
                        fontSize: "16px",
                        marginRight: 2,
                      },
                    }}
                  /> */}
                    <Box sx={{ flex: "1 1 100%" }}>
                      <Button
                        variant="contained"
                        sx={{ flex: "0 1 30%", float: "right" }}
                        onClick={handleUserUpdate}
                      >
                        Save Changes
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {(user.role === "Admin" || user.role === "Super Admin") && (
                <Box
                  sx={{
                    backgroundColor: "#f5faff",
                    padding: 2,
                    borderRadius: 2,
                    marginBottom: 2,
                    gap: 2,
                  }}
                >
                  {" "}
                  <Typography variant="subtitle1" marginBottom={2} color="grey">
                    Roles and Permission Management
                  </Typography>
                  {roles.map((role) => (
                    <React.Fragment key={role.role}>
                      <Box
                        sx={{
                          backgroundColor: "#f5faff",
                          padding: 2,
                          borderRadius: 2,
                          marginBottom: 2,
                          gap: 2,
                          display: "flex",
                          border: "1px solid #ccc",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          color="grey"
                          sx={{ width: "18%" }}
                        >
                          {role.role} :
                        </Typography>
                        <FormControl sx={{ flex: "1 1 Calc(50% - 8px)" }}>
                          <InputLabel
                            shrink
                            sx={{
                              backgroundColor: "#f5faff",
                              padding: "0px 4px",
                            }}
                          >
                            Access Permissions
                          </InputLabel>
                          <Autocomplete
                            multiple
                            options={constants.PERMISSIONS}
                            getOptionLabel={(option) => option}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select permissions"
                                variant="outlined"
                              />
                            )}
                            value={role.permissions}
                            onChange={(_, newPermissions) =>
                              handlePermissionsChange(role.role, newPermissions)
                            }
                          />
                        </FormControl>
                      </Box>
                    </React.Fragment>
                  ))}
                  <Box
                    sx={{
                      flex: "1 1 100%",
                      backgroundColor: "#f5faff",
                      borderRadius: 2,
                      marginBottom: 7,
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{ flex: "0 1 30%", float: "right" }}

                      onClick={handleRoleUpdate}
                    >
                      Save Changes
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Settings;
